import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { environment } from '../../environments';

const MESSAGES: Record<string, string> = {
  'dealer-not-found': 'Your dealership does not exist in REM. Please contact your REM administrator to have it registered.',
  'nsc-not-found': 'Your NSC does not exist in REM. Please contact your REM administrator to have it registered.',
  'invalid-dealer-code': 'Your user account does not contain a valid dealer code, so REM cannot identify your dealership. Please contact your BMW administrator.',
  'unsupported-role': 'Your user account does not have a REM role (dealer or NSC). Please contact your BMW administrator.',
  'access-denied': 'Your account does not have access to REM. Please contact your BMW administrator.',
  'too-many-attempts': 'We could not sign you in to REM. Please return to the launching application and try again.',
  'authentication-failed': 'Authentication failed. Please return to the launching application and try again.'
};

const BACKEND_CODE_TO_REASON: Record<string, string> = {
  REM_DEALER_NOT_FOUND: 'dealer-not-found',
  REM_NSC_NOT_FOUND: 'nsc-not-found',
  REM_INVALID_DEALER_CODE: 'invalid-dealer-code',
  REM_UNSUPPORTED_ROLE: 'unsupported-role'
};

/** Maps a failed /Auth response to an error-page reason; the backend text itself is never displayed. */
export function getAuthErrorReason(error: unknown): string {
  if (!(error instanceof HttpErrorResponse) || error.status !== 403) {
    return 'authentication-failed';
  }
  return BACKEND_CODE_TO_REASON[error.error?.code] ?? 'access-denied';
}

/** Terminal auth error page; it must never start a sign-in, otherwise failures loop through Cognito. */
@Component({
  selector: 'app-auth-error',
  standalone: true,
  template: `
    <main class="auth-error">
      <h1>Sign-in problem</h1>
      <p role="alert">{{ message }}</p>
      <a [href]="returnUrl">Return to the launching application</a>
    </main>
  `,
  styles: [`
    .auth-error { max-width: 36rem; margin: 4rem auto; padding: 0 1rem; font-family: inherit; color: #16181d; }
    h1 { font-size: 1.25rem; margin-bottom: 1rem; }
    a { color: #1c69d4; }
    a:focus-visible { outline: 2px solid #1c69d4; outline-offset: 2px; }
  `]
})
export class AuthErrorComponent {
  readonly returnUrl = environment.postLogoutUrl;
  readonly message: string;

  constructor() {
    const reason = inject(ActivatedRoute).snapshot.queryParamMap.get('reason') ?? '';
    // Unknown reasons fall back to the generic text so query params can't inject content.
    this.message = MESSAGES[reason] ?? MESSAGES['authentication-failed'];
  }
}
